import React, {useState, useEffect, useRef} from 'react';
import Modal from 'react-modal';

import Button, {ButtonTypes} from './ButtonNew';
import {sendVerificationEmail, verifyEmailCode} from '../api/api';
import {useAppContext, useAnalytics} from '../hooks';
import CollapserThing from './CollapserThing';
import {getBrandName} from './Brand';

const AccountSharingModal = () => {
  const {currentUser} = useAppContext();
  const {tracker} = useAnalytics();
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [canResendEmail, setCanResendEmail] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verificationError, setVerificationError] = useState('');
  const resendTimeoutRef = useRef(null);
  const shownRef = useRef(false);

  useEffect(() => {
    // The script injected by the Unshared middleware dispatches this event
    // when the current user is flagged for account sharing, either from its
    // status poll or from an intercepted `account_flagged` 403 response
    const onFlagged = () => {
      if (shownRef.current) return;
      shownRef.current = true;
      tracker.accountSharingModalView();
      setShowModal(true);
    };

    window.addEventListener('unshared:flagged', onFlagged);

    return () => {
      window.removeEventListener('unshared:flagged', onFlagged);
      clearTimeout(resendTimeoutRef.current);
    };
  }, []);

  const handleSendEmail = async () => {
    setLoading(true);
    setVerificationError('');
    tracker.accountSharingModalEmailSent({isResend: emailSent});

    try {
      const response = await sendVerificationEmail();
      if (!response.success) throw new Error(response.error?.message || 'Failed to send verification email');
      setEmailSent(true);
      setCanResendEmail(false);

      // Clear existing timeout and set new one
      clearTimeout(resendTimeoutRef.current);
      resendTimeoutRef.current = setTimeout(() => setCanResendEmail(true), 20000);
    } catch (error) {
      setVerificationError('Failed to send verification email. Please contact us at support@aced.io');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    setVerifyLoading(true);
    setVerificationError('');

    try {
      const response = await verifyEmailCode(verificationCode);

      if (response.success && response.data?.verified) {
        tracker.accountSharingModalCodeSubmitted({success: true});
        // The server has already flipped the verdict; reload to re-fetch content
        window.location.reload();
      } else {
        tracker.accountSharingModalCodeSubmitted({success: false});
        setVerificationError('Invalid verification code. Please try again.');
      }
    } catch (error) {
      tracker.accountSharingModalCodeSubmitted({success: false});
      setVerificationError('Failed to verify code. Please contact us at support@aced.io');
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setVerificationCode(value);
    setVerificationError('');
  };

  return showModal ? (
    <Modal
      overlayClassName="fixed inset-0 bg-black/40 flex justify-center items-center z-50"
      className="bg-white w-full h-screen sm:h-auto sm:max-w-lg p-8 sm:rounded-xl shadow-xl outline-none z-50"
      isOpen
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEsc={false}
    >
      <div className="text-center">
        <div className="mb-6">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-yellow-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Let&apos;s verify your account
          </h1>
          <p className="text-gray-600 mb-6">
            {`We've detected unusual activity on your account and need to verify your email address before you can continue using ${getBrandName()}.`}
          </p>
        </div>

        {!emailSent ? (
          <div>
            <p className="text-sm text-gray-500 mb-6">
              We&apos;ll send a 6-digit verification code to
              {' '}
              <span className="font-semibold">{currentUser?.email}</span>
            </p>

            <Button
              onClick={handleSendEmail}
              loading={loading}
              className="w-full"
            >
              Send verification email
            </Button>
          </div>
        ) : (
          <div>
            <p className="text-sm text-gray-600 mb-4">
              We&apos;ve sent a 6-digit verification code to your email address.
            </p>
            <div className="mb-4">
              <label htmlFor="verification-code" className="block text-sm font-medium text-gray-700 mb-2">
                Enter verification code
              </label>

              <input
                type="text"
                id="verification-code"
                value={verificationCode}
                onChange={handleCodeChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-center text-xl font-mono tracking-widest focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="000000"
                maxLength={6}
                autoComplete="off"
              />
            </div>

            <Button
              onClick={handleVerifyCode}
              loading={verifyLoading}
              disabled={verificationCode.length !== 6}
              className="w-full"
            >
              Verify code
            </Button>

            <CollapserThing visible={canResendEmail} className="mt-3">
              <Button
                onClick={handleSendEmail}
                buttonType={ButtonTypes.TERTIARY}
                className="w-full"
              >
                {loading ? 'Sending...' : "Didn't receive the email? Send again"}
              </Button>
            </CollapserThing>
          </div>
        )}

        <Button
          href="/logout"
          buttonType={ButtonTypes.SECONDARY}
          className="mt-3 w-full"
        >
          Log out
        </Button>

        {verificationError && (
          <div className="mt-3 p-3 bg-red-50 rounded-lg">
            <p className="text-sm text-red-600">{verificationError}</p>
          </div>
        )}
      </div>
    </Modal>
  ) : null;
};

export default AccountSharingModal;
